/// <reference types="vite/client" />
// Fee calculation — pure functions for computing ChainSale payment fees.
// All USD-denominated minimums enforced here; on-chain contract applies rate only.

import type { NetworkId } from '../types/network.ts';

// --- Fee configuration (from .env at build time) ---

const FEE_RATE = 0.0005;           // 0.05% total
const FEE_RATE_BPS = 5;            // ChainSaleFee.feeRateBps — the on-chain FLOOR (v2 takes an explicit fee ≥ this)
const HALF_RATE = FEE_RATE / 2;    // 0.025% per side
const MIN_HALF_FEE_USD = 0.025;    // 2.5 cents per side
const MIN_TX_USD = 0.50;           // $0.50 minimum transaction

// --- Fee contract addresses per network ---

const FEE_CONTRACTS: Readonly<Partial<Record<NetworkId, string>>> = Object.freeze({
  ethereum:  import.meta.env.VITE_FEE_CONTRACT_ETHEREUM  ?? '',
  polygon:   import.meta.env.VITE_FEE_CONTRACT_POLYGON   ?? '',
  arbitrum:  import.meta.env.VITE_FEE_CONTRACT_ARBITRUM  ?? '',
  optimism:  import.meta.env.VITE_FEE_CONTRACT_OPTIMISM  ?? '',
  base:      import.meta.env.VITE_FEE_CONTRACT_BASE      ?? '',
  bsc:       import.meta.env.VITE_FEE_CONTRACT_BSC       ?? '',
  avalanche: import.meta.env.VITE_FEE_CONTRACT_AVALANCHE ?? '',
  hyperevm:  import.meta.env.VITE_FEE_CONTRACT_HYPEREVM  ?? '',
});

// --- Types ---

export type FeeBreakdown = Readonly<{
  readonly senderSurchargeUsd: number;
  readonly recipientDeductionUsd: number;
  readonly totalFeeUsd: number;
  readonly belowMinimumTx: boolean;
}>;

export type FeeInBaseUnits = Readonly<{
  readonly senderSurcharge: bigint;     // added on top for the payer
  readonly recipientDeduction: bigint;  // taken from the recipient's share
  readonly totalToSend: bigint;         // baseAmount + senderSurcharge — what the payer's wallet sends
  readonly fee: bigint;                 // passed to the contract: surcharge + deduction, lifted to the floor
  readonly forward: bigint;             // what the recipient receives = totalToSend - fee
}>;

// --- Pure fee calculation ---

export const calculateFeeUsd = (amountUsd: number): FeeBreakdown => {
  if (amountUsd < MIN_TX_USD) {
    return Object.freeze({
      senderSurchargeUsd: 0,
      recipientDeductionUsd: 0,
      totalFeeUsd: 0,
      belowMinimumTx: true,
    });
  }

  const senderSurchargeUsd = Math.max(amountUsd * HALF_RATE, MIN_HALF_FEE_USD);
  const recipientDeductionUsd = Math.max(amountUsd * HALF_RATE, MIN_HALF_FEE_USD);

  return Object.freeze({
    senderSurchargeUsd,
    recipientDeductionUsd,
    totalFeeUsd: senderSurchargeUsd + recipientDeductionUsd,
    belowMinimumTx: false,
  });
};

// Convert sender surcharge from USD to base units and compute total send amount
export const calculateFeeBaseUnits = (
  baseAmount: bigint,
  amountUsd: number,
  decimals: number,
  exchangeRateUsd: number,
): FeeInBaseUnits => {
  const { senderSurchargeUsd, recipientDeductionUsd, belowMinimumTx } = calculateFeeUsd(amountUsd);
  const priced = !belowMinimumTx && exchangeRateUsd > 0;

  // USD → crypto base units: usd / rate * 10^decimals (floored)
  const toUnits = (usd: number): bigint =>
    priced ? BigInt(Math.floor((usd / exchangeRateUsd) * (10 ** decimals))) : 0n;
  const senderSurcharge = toUnits(senderSurchargeUsd);
  const recipientDeduction = toUnits(recipientDeductionUsd);

  const totalToSend = baseAmount + senderSurcharge;
  // The contract floor is a rate of the TOTAL (surcharge included), a hair above the two halves
  // of the base — lift to it so the contract never rejects the payment
  const fee = [senderSurcharge + recipientDeduction, contractFeeFloor(totalToSend)]
    .reduce((a, b) => (a > b ? a : b));

  return Object.freeze({ senderSurcharge, recipientDeduction, totalToSend, fee, forward: totalToSend - fee });
};

// ChainSaleFee v2 rejects fee < amount * feeRateBps / 10000 (integer floor) — mirror of _checkFee
export const contractFeeFloor = (total: bigint, feeRateBps: number = FEE_RATE_BPS): bigint =>
  (total * BigInt(feeRateBps)) / 10000n;

// --- Contract address lookup ---

export const getFeeContractAddress = (networkId: NetworkId): string | null => {
  const address = FEE_CONTRACTS[networkId];
  return address && address.length > 0 ? address : null;
};

// --- Minimum transaction check ---

export const isBelowMinimumTx = (amountUsd: number): boolean => amountUsd < MIN_TX_USD;

export const MIN_TRANSACTION_USD = MIN_TX_USD;
